import React, { useCallback } from 'react';
import { IconX } from '@tabler/icons';
import { ActionIcon, Button } from '@mantine/core';
import {
  IPillOption,
  PillsInputAutoCompleteWithQuery
} from '@/containers/pages/filials/edit/components/tabs/security/components/share-links/components/PillsInputAutoCompleteWithQuery';
import { useAppSelector } from '@hooks/redux/redux';
import { IFiltersEnumType } from '@/types/enums';
import { useTranslation } from 'react-i18next';
import classes from './RulesSettings.module.css';
import { useRolesSettings } from '@/containers/pages/filials/edit/components/tabs/security/components/restrict-rooms-by-roles/components/RulesRestrictions/useRolesSettings';
import { RoomsRole, RoomsRoleAccess } from '@/services/api/customerSettingsApiArea/types';
import { roleApiArea } from '@/services/api/roleApiArea';

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  settings: RoomsRoleAccess | undefined;

  onSuccess(): Promise<void>;
}

export const RulesRestrictions = (props: Props) => {
  const { settings, onSuccess } = props;
  const { t } = useTranslation();
  const { filial } = useAppSelector((state) => state.filials);
  const onClose = () => props.setIsOpen(false);

  const [isLoading, handleSubmit, roles, setRoles] = useRolesSettings({
    onClose,
    settings,
    onSuccess,
    customerId: filial?.Id
  });

  const loader = useCallback(
    (query: string) => {
      return roleApiArea().GetRolesByName({
        Query: query,
        FilterConditions: filial
          ? [
              {
                Type: IFiltersEnumType.FilialFilter,
                NumValues: [filial.Id]
              }
            ]
          : []
      });
    },
    [filial?.Id]
  );

  const handleValueChange = useCallback((items: IPillOption<RoomsRole>[]) => {
    setRoles(
      items.map((item) => ({
        Id: item.value,
        Name: item.label
      }))
    );
  }, []);

  const handleMapDataToOptions = useCallback(
    (item: RoomsRole) => ({
      label: item.Name,
      value: item.Id
    }),
    []
  );

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className="title">{t('RestrictRoomsByRoles.RulesRestrictionPopUp.title')}</div>
        <ActionIcon onClick={onClose}>
          <IconX size={16} />
        </ActionIcon>
      </div>
      <div className={classes.body}>
        <div className={classes.part}>
          <div className={classes.subTitle}>
            {t('RestrictRoomsByRoles.RulesRestrictionPopUp.roles')}
          </div>
          <PillsInputAutoCompleteWithQuery<IPillOption<RoomsRole>, RoomsRole>
            loaderData={loader}
            onValueChange={handleValueChange}
            defaultValues={roles.map(handleMapDataToOptions)}
            mapDataToOptions={handleMapDataToOptions}
            props={{
              PillsInputFieldProps: {
                placeholder: t('RestrictRoomsByRoles.RulesRestrictionPopUp.chooseRoles')
              }
            }}
          />
        </div>
      </div>
      <div className={classes.footer}>
        <Button fullWidth size="sm" radius={8} loading={isLoading} onClick={handleSubmit}>
          {t('RestrictRoomsByRoles.RulesRestrictionPopUp.save')}
        </Button>
        <Button fullWidth size="sm" color="grey" radius={8} variant="light" onClick={onClose}>
          {t('RestrictRoomsByRoles.RulesRestrictionPopUp.cancel')}
        </Button>
      </div>
    </div>
  );
};
